import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, LinearProgress, MenuItem, Paper, Stack, Switch, Tab, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import adminApi from '../../services/adminApi';

const statusColor = (s?: string): 'success' | 'warning' | 'error' | 'default' => {
  const v = (s || '').toLowerCase();
  if (['ok', 'online', 'connected', 'healthy', 'up'].includes(v)) return 'success';
  if (['degraded', 'warning', 'slow'].includes(v)) return 'warning';
  if (['down', 'error', 'offline', 'disconnected'].includes(v)) return 'error';
  return 'default';
};

const AdminSettingsPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  // ---- System Health ----
  const [health, setHealth] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState('');

  const loadHealth = useCallback(async () => {
    try {
      setHealthLoading(true); setHealthError('');
      const res = await adminApi.get('/system-status');
      setHealth(res.data.data || res.data);
    } catch (e: any) {
      setHealthError(e?.response?.data?.message || 'Could not load system status.');
    } finally { setHealthLoading(false); }
  }, []);

  // ---- Admin Management ----
  const [admins, setAdmins] = useState<any[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [editing, setEditing] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<any>({ email: '', password: '', firstName: '', lastName: '', role: 'admin' });
  const [saving, setSaving] = useState(false);

  const loadAdmins = useCallback(async () => {
    try {
      setAdminsLoading(true); setAdminsError('');
      const res = await adminApi.get('/admins');
      setAdmins(res.data.data || []);
    } catch (e: any) {
      setAdminsError(e?.response?.data?.message || 'Could not load admins.');
    } finally { setAdminsLoading(false); }
  }, []);

  const toggleActive = async (a: any) => {
    try { await adminApi.put('/admins/' + a.id + '/status', { isActive: !a.isActive }); await loadAdmins(); }
    catch (e: any) { setAdminsError(e?.response?.data?.message || 'Failed to update status.'); }
  };
  const removeAdmin = async (a: any) => {
    if (!window.confirm('Delete admin ' + (a.email || a.id) + '? This cannot be undone.')) return;
    try { await adminApi.delete('/admins/' + a.id); await loadAdmins(); }
    catch (e: any) { setAdminsError(e?.response?.data?.message || 'Failed to delete admin.'); }
  };
  const openEdit = (a: any) => { setForm({ firstName: a.firstName || '', lastName: a.lastName || '', role: a.role || 'admin' }); setEditing(a); };
  const openCreate = () => { setForm({ email: '', password: '', firstName: '', lastName: '', role: 'admin' }); setCreating(true); };
  const submitEdit = async () => {
    try { setSaving(true); await adminApi.put('/admins/' + editing.id, { firstName: form.firstName, lastName: form.lastName, role: form.role }); setEditing(null); await loadAdmins(); }
    catch (e: any) { setAdminsError(e?.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };
  const submitCreate = async () => {
    try {
      setSaving(true);
      await adminApi.post('/create', {
        email: form.email, password: form.password, firstName: form.firstName,
        lastName: form.lastName, role: form.role, username: (form.email || '').split('@')[0],
      });
      setCreating(false); await loadAdmins();
    } catch (e: any) { setAdminsError(e?.response?.data?.message || 'Failed to create admin.'); }
    finally { setSaving(false); }
  };

  // ---- Platform Settings ----
  const [settings, setSettings] = useState<any>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsMsg, setSettingsMsg] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try { setSettingsLoading(true); setSettingsError('');
      const res = await adminApi.get('/settings'); setSettings(res.data.data || res.data);
    } catch (e: any) { setSettingsError(e?.response?.data?.message || 'Could not load settings.'); }
    finally { setSettingsLoading(false); }
  }, []);
  const saveSettings = async () => {
    try { setSettingsSaving(true); setSettingsMsg(''); setSettingsError('');
      const res = await adminApi.put('/settings', settings); setSettings(res.data.data || res.data);
      setSettingsMsg('Settings saved.');
    } catch (e: any) { setSettingsError(e?.response?.data?.message || 'Failed to save settings.'); }
    finally { setSettingsSaving(false); }
  };

  useEffect(() => {
    if (tab === 0) loadHealth();
    if (tab === 1) loadAdmins();
    if (tab === 2) loadSettings();
  }, [tab, loadHealth, loadAdmins, loadSettings]);

  const sys = health?.system || {};
  const stats = health?.statistics || {};
  const recentErrors: any[] = health?.monitoring?.recentErrors || [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Admin Settings & System Health</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Monitor system performance and manage admin accounts
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab label="System Health" />
          <Tab label="Admin Management" />
          <Tab label="Platform Settings" />
        </Tabs>
      </Paper>

      {/* SYSTEM HEALTH */}
      {tab === 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">System Health</Typography>
            <Button size="small" onClick={loadHealth} disabled={healthLoading}>Refresh</Button>
          </Stack>
          {healthError && <Alert severity="error" sx={{ mb: 2 }}>{healthError}</Alert>}
          {healthLoading && !health ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Stack spacing={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Services</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Chip label={'Database: ' + (sys?.database?.status || 'unknown')} color={statusColor(sys?.database?.status)} />
                  <Chip label={'Redis: ' + (sys?.redis?.status || sys?.redis || 'unknown')} color={statusColor(sys?.redis?.status || sys?.redis)} />
                  <Chip label={'Env: ' + (sys?.environment || '—')} />
                  <Chip label={'Version: ' + (sys?.version || '—')} />
                </Stack>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Statistics</Typography>
                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                  <Box><Typography variant="h5">{stats.totalUsers ?? sys.totalUsers ?? '—'}</Typography><Typography variant="caption" color="text.secondary">Total Users</Typography></Box>
                  <Box><Typography variant="h5">{stats.totalTransactions ?? sys.totalTransactions ?? '—'}</Typography><Typography variant="caption" color="text.secondary">Total Transactions</Typography></Box>
                  <Box><Typography variant="h5">{sys.activeSessions ?? '—'}</Typography><Typography variant="caption" color="text.secondary">Active Sessions</Typography></Box>
                </Stack>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Recent Errors</Typography>
                {recentErrors.length === 0 ? <Typography variant="body2" color="text.secondary">No recent errors.</Typography> : (
                  <TableContainer><Table size="small">
                    <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Type</TableCell><TableCell>Message</TableCell></TableRow></TableHead>
                    <TableBody>
                      {recentErrors.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.timestamp || e.time || e.createdAt || '—'}</TableCell>
                          <TableCell>{e.type || e.level || e.code || '—'}</TableCell>
                          <TableCell>{e.message || e.error || JSON.stringify(e).slice(0, 80)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></TableContainer>
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      )}

      {/* ADMIN MANAGEMENT */}
      {tab === 1 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Admin Accounts ({admins.length})</Typography>
            <Button variant="contained" onClick={openCreate}>+ Create Admin</Button>
          </Stack>
          {adminsError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAdminsError('')}>{adminsError}</Alert>}
          {adminsLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Paper><TableContainer><Table>
              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {admins.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{[a.firstName, a.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                    <TableCell>{a.email}</TableCell>
                    <TableCell><Chip size="small" label={a.role} /></TableCell>
                    <TableCell><Chip size="small" label={a.isActive ? 'Active' : 'Inactive'} color={a.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEdit(a)}>Edit</Button>
                      <Button size="small" onClick={() => toggleActive(a)}>{a.isActive ? 'Deactivate' : 'Activate'}</Button>
                      <Button size="small" color="error" onClick={() => removeAdmin(a)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {admins.length === 0 && <TableRow><TableCell colSpan={5} align="center">No admins found.</TableCell></TableRow>}
              </TableBody>
            </Table></TableContainer></Paper>
          )}

          <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
            <DialogTitle>Edit Admin</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField label="First Name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} fullWidth />
                <TextField label="Last Name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} fullWidth />
                <TextField select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} fullWidth>
                  {['super_admin', 'admin', 'manager', 'support', 'moderator'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Stack>
            </DialogContent>
            <DialogActions><Button onClick={() => setEditing(null)}>Cancel</Button><Button variant="contained" onClick={submitEdit} disabled={saving}>Save</Button></DialogActions>
          </Dialog>

          <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="sm">
            <DialogTitle>Create Admin</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth required />
                <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} fullWidth required />
                <TextField label="First Name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} fullWidth />
                <TextField label="Last Name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} fullWidth />
                <TextField select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} fullWidth>
                  {['super_admin', 'admin', 'manager', 'support', 'moderator'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Stack>
            </DialogContent>
            <DialogActions><Button onClick={() => setCreating(false)}>Cancel</Button><Button variant="contained" onClick={submitCreate} disabled={saving}>Create</Button></DialogActions>
          </Dialog>
        </Box>
      )}

      {/* PLATFORM SETTINGS */}
      {tab === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Platform Settings</Typography>
          {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}
          {settingsMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSettingsMsg('')}>{settingsMsg}</Alert>}
          {settingsLoading || !settings ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Stack spacing={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Transaction Settings</Typography>
                <TextField label="Transaction Fee (%)" type="number" value={settings.transactionFeePercent ?? 0}
                  onChange={(e) => setSettings({ ...settings, transactionFeePercent: e.target.value === '' ? '' : Number(e.target.value) })}
                  sx={{ maxWidth: 240 }} inputProps={{ min: 0, max: 100, step: 0.1 }} />
                <FormRow label="Require KYC Verification" checked={!!settings.requireKyc} onChange={(v) => setSettings({ ...settings, requireKyc: v })} />
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>System Settings</Typography>
                <FormRow label="Maintenance Mode" checked={!!settings.maintenanceMode} onChange={(v) => setSettings({ ...settings, maintenanceMode: v })} />
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Notification Settings</Typography>
                <FormRow label="Email Notifications" checked={!!settings.emailNotifications} onChange={(v) => setSettings({ ...settings, emailNotifications: v })} />
                <FormRow label="SMS Notifications" checked={!!settings.smsNotifications} onChange={(v) => setSettings({ ...settings, smsNotifications: v })} />
              </Paper>
              <Box><Button variant="contained" onClick={saveSettings} disabled={settingsSaving}>{settingsSaving ? 'Saving...' : 'Save Settings'}</Button></Box>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

const FormRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <Typography>{label}</Typography>
  </Stack>
);

export default AdminSettingsPage;
